import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import { MdGroup } from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import { formatPhone } from '../../ui/phone';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { FormGrid, Field, Input, Select, Textarea, FormSection, RadioGroup } from '../../ui/Form';
import PhoneInput from '../../ui/PhoneInput';
import SearchableSelect from '../../ui/SearchableSelect';
import { sectionOptions } from '../../ui/labels';
import ReactivateStudentModal from './ReactivateStudentModal';
import './students.css';
import { useNavigate } from '../navIntent';
import { useAuth } from '../auth';
import HelpButton from '../help/HelpButton';

const GENDERS = [{ value: 'male', label: 'ذكر' }, { value: 'female', label: 'أنثى' }];
const LEAVE_REASONS = ['نقل', 'سفر', 'ترك الدراسة', 'غير ذلك'];

const emptyStudent = {
  // given_name is what the office actually types; `name` is what gets
  // saved and is composed from it + the family (father_name, last_name)
  // — but name stays directly editable, because real sibling records
  // drift in spelling ("خطيب" vs "الخطيب") and forcing it would fight
  // that instead of fixing it.
  given_name: '', name: '', gender: '', birth_date: '', birth_place: '', national_id: '',
  student_phone: '', registration_number: '', notes: '', tuition_fee: 0, transport_fee: 0,
  transportation_id: '', family_id: '',
};

// The new family's fields, filled inline in the student form itself
// rather than in a modal on top of a modal. Saved as a real family
// (POST /families) just before the student, only when mode is 'new'.
const emptyNewFamily = {
  father_name: '', father_phone: '', father_job: '', last_name: '',
  mother_name: '', mother_phone: '', mother_last_name: '',
  address: '', amana: '', notes: '',
};

const FAMILY_MODES = [
  { value: 'existing', label: 'عائلة موجودة' },
  { value: 'new', label: '+ عائلة جديدة' },
];

/** name = given name + father's name + surname, in Arabic order,
 *  skipping any part that's blank (no family picked yet, or the
 *  family has no surname on file). */
function composeStudentName(givenName, family) {
  return [givenName, family?.father_name, family?.last_name]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(' ');
}

export default function Students() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const toast = useToastWithSound();
  // The student rows already carry section_label from the server, so
  // there is no enrollments fetch and no client-side join here. Sections
  // are still loaded for the transfer picker, families for the
  // family-link picker in the student form.
  const { data, loading, error, reload } = useData(async () => {
    const [students, sections, families, transportation] = await Promise.all([
      // include_inactive: the server leaves departed pupils out by
      // default, so without it the table's «مؤرشف» filter was always empty.
      api.students({ include_inactive: 1 }), api.sections(), api.families(), api.transportation(),
    ]);
    return { students, sections, families, transportation };
  }, []);

  const [editing, setEditing] = useState(null); // student object or {} for new
  const [form, setForm] = useState(emptyStudent);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [leaveReason, setLeaveReason] = useState('نقل');
  const [transferring, setTransferring] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [newSectionId, setNewSectionId] = useState('');
  const [newGradeId, setNewGradeId] = useState('');
  const [busy, setBusy] = useState(false);
  // 'existing' = pick from the list; 'new' = type the family inline in
  // this same form (no second modal on top of this one).
  const [familyMode, setFamilyMode] = useState('existing');
  const [newFamily, setNewFamily] = useState(emptyNewFamily);
  // Once the office edits the composed name by hand, stop overwriting
  // it when they change the given name or the family afterward — same
  // "auto until touched, then it's yours" rule NameField uses.
  const [nameEditedManually, setNameEditedManually] = useState(false);
  // Both halves of the student modal are tracked together: typing only
  // in the inline new-family fields still counts as unsaved work.
  const dirty = useDirtyForm({ form, newFamily });
  const transferDirty = useDirtyForm(newSectionId);
  const archiveDirty = useDirtyForm(leaveReason);

  const sections = data?.sections || [];
  const families = data?.families || [];
  const buses = (data?.transportation || []).filter((t) => t.status === 'active');
  const busOptions = buses.map((t) => ({
    value: t.id,
    label: `${t.bus_number} — ${t.driver_name}${t.monthly_fee == null ? '' : ` ($${t.monthly_fee.toLocaleString('en-US')}/شهر)`}`,
  }));

  // Labelled with BOTH parents' names and the phone, since the office
  // usually remembers the family by one parent's name and needs the
  // phone to be sure it's the right household when names repeat.
  const familyOptions = families.map((f) => ({
    value: f.id,
    label: `${[f.father_name, f.mother_name].filter(Boolean).join(' و ') || `عائلة #${f.id}`}`
      + (f.father_phone || f.mother_phone ? ` — ${f.father_phone || f.mother_phone}` : '')
      + (f.student_count > 0 ? ` (${f.student_count} طلاب)` : ''),
  }));
  // In 'new' mode the family doesn't exist yet, so the name composition
  // reads the inline draft instead of the saved list — typing the
  // father's name there updates the composed full name immediately,
  // exactly as picking an existing family does.
  const selectedFamily = familyMode === 'new'
    ? newFamily
    : families.find((f) => String(f.id) === String(form.family_id));

  const rows = (data?.students || []).map((s) => ({
    ...s,
    section_title: s.section_label || '—',
  }));

  const openNew = () => {
    setForm(emptyStudent);
    setNewFamily(emptyNewFamily);
    setFamilyMode('existing');
    setNameEditedManually(false);
    dirty.reset({ form: emptyStudent, newFamily: emptyNewFamily });
    setEditing({});
  };
  const openEdit = (s) => {
    // Guess the given name by stripping the linked family's
    // father_name/last_name off the end of the saved full name — only
    // feeds the helper's given-name box; `name` itself is already
    // correct on screen and unaffected if the guess is imperfect.
    const fam = families.find((x) => x.id === s.family_id);
    const suffix = [fam?.father_name, fam?.last_name].filter(Boolean).join(' ');
    const givenGuess = suffix && s.name?.endsWith(suffix)
      ? s.name.slice(0, s.name.length - suffix.length).trim()
      : (s.name || '');
    const next = {
      given_name: givenGuess,
      name: s.name || '', gender: s.gender || '', birth_date: s.birth_date || '',
      birth_place: s.birth_place || '', national_id: s.national_id || '',
      student_phone: s.student_phone || '', registration_number: s.registration_number || '',
      notes: s.notes || '', tuition_fee: s.tuition_fee || 0, transport_fee: s.transport_fee || 0,
      transportation_id: s.transportation_id || '', family_id: s.family_id || '',
    };
    setForm(next);
    setNewFamily(emptyNewFamily);
    setFamilyMode('existing');
    // An existing student's saved name is treated as already
    // deliberate — editing their family afterward must NOT silently
    // rewrite a name the office already reviewed and possibly hand-
    // corrected for spelling.
    setNameEditedManually(true);
    dirty.reset({ form: next, newFamily: emptyNewFamily });
    setEditing(s);
  };

  // Re-compose the full name from given_name + the selected family,
  // UNLESS the office has already typed directly into the full-name
  // box this session (nameEditedManually) — that always wins.
  const setGivenName = (v) => {
    setForm((f) => {
      const next = { ...f, given_name: v };
      if (!nameEditedManually) next.name = composeStudentName(v, selectedFamily);
      return next;
    });
  };
  const setFamilyId = (v) => {
    setForm((f) => {
      const next = { ...f, family_id: v };
      if (!nameEditedManually) {
        const fam = families.find((x) => String(x.id) === String(v));
        next.name = composeStudentName(f.given_name, fam);
      }
      return next;
    });
  };
  const setNameManually = (v) => {
    setNameEditedManually(true);
    setForm((f) => ({ ...f, name: v }));
  };

  // Picking a bus copies its current monthly_fee onto transport_fee so
  // the office sees the real number immediately; clearing the bus
  // (value === '') zeroes the fee, matching "no transportation used".
  const setBusId = (e) => {
    const v = e.target.value;
    const bus = buses.find((b) => String(b.id) === String(v));
    setForm((f) => ({ ...f, transportation_id: v, transport_fee: bus ? bus.monthly_fee : 0 }));
  };

  // Editing the inline new family's father_name/last_name recomposes the
  // student's full name, matching what picking an existing family does.
  const setNewFamilyField = (k) => (e) => {
    const v = e.target.value;
    setNewFamily((prev) => {
      const nextFamily = { ...prev, [k]: v };
      if (!nameEditedManually && (k === 'father_name' || k === 'last_name')) {
        setForm((f) => ({ ...f, name: composeStudentName(f.given_name, nextFamily) }));
      }
      return nextFamily;
    });
  };

  const switchFamilyMode = (mode) => {
    setFamilyMode(mode);
    // Leaving a half-typed new family behind would silently create it on
    // save; clearing the other side's value keeps exactly one source of
    // truth for which family this student gets.
    if (mode === 'new') {
      setForm((f) => ({ ...f, family_id: '' }));
    } else {
      setNewFamily(emptyNewFamily);
    }
    if (!nameEditedManually) {
      setForm((f) => ({ ...f, name: composeStudentName(f.given_name, mode === 'new' ? emptyNewFamily : null) }));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const creatingInlineFamily = familyMode === 'new';
    if (creatingInlineFamily && !newFamily.father_name.trim() && !newFamily.mother_name.trim()) {
      return toast.error('اسم الأب أو الأم مطلوب على الأقل لإضافة عائلة جديدة');
    }
    if (!editing.id && !creatingInlineFamily && !form.family_id) {
      return toast.error('يجب اختيار العائلة أو إضافتها أولاً');
    }
    if (!form.name.trim()) return toast.error('الاسم مطلوب');
    setSaving(true);
    // family_id and transportation_id are split out: createStudent
    // accepts family_id directly (transport starts unset on a new
    // student), but an existing student's family/bus only ever move
    // through their own dedicated endpoints (setStudentFamily needs its
    // own FK check and updates family_summaries; setStudentTransportation
    // needs to read the bus's current monthly_fee). given_name is UI-only
    // scaffolding for composing `name` — never sent or stored.
    const { family_id, transportation_id, given_name, ...fields } = form;
    try {
      // The inline family is created first, so the student it belongs to
      // has a real family_id to link to in the very next call.
      let familyId = family_id ? Number(family_id) : null;
      if (creatingInlineFamily) {
        const fam = await api.createFamily(newFamily);
        familyId = fam.id;
      }
      const busId = transportation_id ? Number(transportation_id) : null;

      if (editing.id) {
        await api.updateStudent(editing.id, fields);
        if (String(familyId ?? '') !== String(editing.family_id || '')) {
          await api.setStudentFamily(editing.id, familyId);
        }
        if (String(busId ?? '') !== String(editing.transportation_id || '')) {
          await api.setStudentTransportation(editing.id, busId);
        }
        toast.success('تم تحديث بيانات الطالب');
      } else {
        const created = await api.createStudent({ ...fields, family_id: familyId });
        if (busId) await api.setStudentTransportation(created.id, busId);
        toast.success(creatingInlineFamily ? 'تمت إضافة العائلة والطالب' : 'تمت إضافة الطالب');
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.deleteStudent(toDelete.id, { leave_reason: leaveReason });
      toast.success('تم أرشفة الطالب');
      setToDelete(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const doTransfer = async (e) => {
    e.preventDefault();
    if (!newSectionId) return toast.error('اختر الشعبة الجديدة');
    setBusy(true);
    try {
      const moved = await api.transferStudent({ student_id: transferring.id, new_section_id: Number(newSectionId) });
      toast.success(moved?.grade_changed ? 'تم نقل الطالب إلى الصف الجديد' : 'تم نقل الطالب');
      setTransferring(null);
      setNewSectionId('');
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  // The grades that have an open section, in school order.
  const transferFromGrade = String(sections.find((x) => x.id === transferring?.section_id)?.grade_id || '');
  const transferGrades = [...new Map(sections.filter((x) => x.status === 'active')
    .sort((a, b) => (a.grade_order ?? 0) - (b.grade_order ?? 0))
    .map((x) => [x.grade_id, { value: String(x.grade_id), label: `${x.grade_title || ''}${String(x.grade_id) === transferFromGrade ? ' (صفه الحالي)' : ''}` }])).values()];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell
      title="الطلاب"
      description="إضافة وتعديل ونقل الطلاب"
      actions={(
        <>
          <HelpButton guides={['student-transfer', 'student-to-section']} />
          {isAdmin && <Button variant="secondary" onClick={() => navigate('student-import')}>استيراد / تحديث من Excel</Button>}
          {isAdmin && <Button onClick={openNew}>+ طالب جديد</Button>}
        </>
      )}
    >
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        guide="students-table"
        keyField="id"
        rows={rows}
        loading={loading}
        statusKey="status"
        emptyText="لا يوجد طلاب"
        searchPlaceholder="ابحث بالاسم أو الرقم الوطني..."
        onEdit={openEdit}
        onDelete={(s) => {
          if (s.status !== 'active') return toast.info('هذا الطالب ملغى قيده مسبقاً — استخدم «تفعيل القيد» لإعادته');
          setLeaveReason('نقل');
          archiveDirty.reset('نقل');
          setToDelete(s);
        }}
        renderActions={(s) => (s.status !== 'active' ? (
          <button type="button" className="ui-btn-mini" title="إعادة الطالب إلى المدرسة وتسجيله في شعبة"
            onClick={() => setReactivating(s)}>
            تفعيل القيد
          </button>
        ) : s.section_id && (
          <button type="button" className="ui-btn-mini" title="نقل إلى شعبة أو صف آخر" data-guide="student-move"
            onClick={() => {
              setNewSectionId('');
              setNewGradeId(String(sections.find((x) => x.id === s.section_id)?.grade_id || ''));
              transferDirty.reset('');
              setTransferring(s);
            }}>
            نقل
          </button>
        ))}
        columns={[
          {
            key: 'name', label: 'الاسم', width: 2, filterable: false,
            render: (r) => (
              <span>
                <strong>{r.name}</strong>
                {r.sibling_count > 0 && (
                  <span className="stu-sibling-badge" title={`لديه ${r.sibling_count} من الإخوة/الأخوات في المدرسة`}>
                    <MdGroup size={11} /> {r.sibling_count}
                  </span>
                )}
              </span>
            ),
          },
          { key: 'section_title', label: 'الشعبة', nowrap: true, align: 'center',
            render: (r) => <span className="ui-badge ui-badge-info">{r.section_title}</span> },
          { key: 'gender', label: 'الجنس', nowrap: true, align: 'center',
            formatValue: (v) => (v === 'male' ? 'ذكر' : v === 'female' ? 'أنثى' : '—') },
          { key: 'national_id', label: 'الرقم الوطني', nowrap: true, filterable: false },
          { key: 'student_phone', label: 'الهاتف', nowrap: true, filterable: false, render: (r) => (r.student_phone ? <bdi dir="ltr">{formatPhone(r.student_phone)}</bdi> : '—') },
          { key: 'status', label: 'الحالة', nowrap: true, align: 'center',
            badge: (v) => (v === 'active' ? 'success' : 'neutral'),
            formatValue: (v) => (v === 'active' ? 'نشط' : 'مؤرشف') },
        ]}
      />

      {editing && (
        <Modal
          open
          size="lg"
          title={editing.id ? 'تعديل بيانات الطالب' : 'طالب جديد'}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving}>حفظ</Button>
            </>
          )}
        >
          <form onSubmit={save}>
            {/* Family FIRST: it's what the given-name field below
                composes the full name from (given + الأب + الكنية), so
                picking it first means the name is right from the start
                instead of being recomposed after the fact. */}
            <FormSection title="العائلة">
              <div className="stu-family-mode">
                <RadioGroup options={FAMILY_MODES} value={familyMode} onChange={switchFamilyMode} />
              </div>

              {familyMode === 'existing' ? (
                <>
                  <Field
                    label="ابحث عن العائلة (اسم الأب أو الأم أو الهاتف)"
                    hint={familyOptions.length === 0 ? undefined : 'اختر عائلة موجودة، أو بدّل إلى «عائلة جديدة» أعلاه إن لم تجدها'}
                    required={!editing.id}
                  >
                    <SearchableSelect
                      value={form.family_id}
                      onChange={setFamilyId}
                      placeholder={editing.id ? 'بلا عائلة مرتبطة' : 'اختر العائلة أولاً'}
                      options={familyOptions}
                      emptyMessage="لم يتم العثور على عائلة بهذا الاسم"
                    />
                  </Field>

                  {selectedFamily && (
                    <div className="stu-family-preview">
                      {selectedFamily.student_count > 1 ? (
                        <>
                          <strong>هذه العائلة لديها {selectedFamily.student_count} طلاب مسجَّلين:</strong>{' '}
                          {selectedFamily.student_names}
                        </>
                      ) : (
                        <span>لا يوجد طلاب آخرون مرتبطون بهذه العائلة بعد.</span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <FormGrid columns={3}>
                    <Field label="اسم الأب"><Input value={newFamily.father_name} onChange={setNewFamilyField('father_name')} /></Field>
                    <Field label="هاتف الأب"><PhoneInput value={newFamily.father_phone} onChange={(v) => setNewFamilyField('father_phone')({ target: { value: v } })} /></Field>
                    <Field label="مهنة الأب"><Input value={newFamily.father_job} onChange={setNewFamilyField('father_job')} /></Field>
                    <Field label="الكنية" hint="تُضاف تلقائياً إلى اسم الطالب"><Input value={newFamily.last_name} onChange={setNewFamilyField('last_name')} /></Field>
                    <Field label="اسم الأم"><Input value={newFamily.mother_name} onChange={setNewFamilyField('mother_name')} /></Field>
                    <Field label="هاتف الأم"><PhoneInput value={newFamily.mother_phone} onChange={(v) => setNewFamilyField('mother_phone')({ target: { value: v } })} /></Field>
                    <Field label="كنية الأم"><Input value={newFamily.mother_last_name} onChange={setNewFamilyField('mother_last_name')} /></Field>
                    <Field label="الأمانة"><Input value={newFamily.amana} onChange={setNewFamilyField('amana')} /></Field>
                    <Field label="العنوان"><Input value={newFamily.address} onChange={setNewFamilyField('address')} /></Field>
                    <Field label="ملاحظات العائلة" span><Textarea value={newFamily.notes} onChange={setNewFamilyField('notes')} rows={2} /></Field>
                  </FormGrid>
                  <div className="stu-family-preview">
                    ستُضاف هذه العائلة تلقائياً عند حفظ الطالب، ويُربط بها مباشرةً.
                  </div>
                </>
              )}
            </FormSection>

            <FormSection title="الاسم">
              <FormGrid columns={2}>
                <Field
                  label="اسم الطالب"
                  required
                  hint="الاسم الأول فقط — اسم الأب والكنية يُضافان تلقائياً من العائلة أعلاه."
                >
                  <Input value={form.given_name} onChange={(e) => setGivenName(e.target.value)} placeholder="محمود" />
                </Field>
                <Field
                  label="الاسم الكامل (يُحفظ)"
                  required
                  hint={
                    !selectedFamily
                      ? 'اختر عائلة أعلاه ليُركَّب تلقائياً، أو اكتبه كاملاً هنا مباشرة.'
                      : nameEditedManually
                        ? 'عُدِّل يدوياً — لن يُستبدل تلقائياً بعد الآن حتى تفتح الطالب من جديد.'
                        : 'مُركَّب تلقائياً: الاسم + اسم الأب + الكنية. يمكنك تعديله للفروق الإملائية بين الإخوة.'
                  }
                >
                  <Input value={form.name} onChange={(e) => setNameManually(e.target.value)} />
                </Field>
              </FormGrid>
            </FormSection>

            <FormSection title="بيانات إضافية">
              <FormGrid columns={3}>
                <Field label="الجنس"><Select value={form.gender} onChange={set('gender')} options={GENDERS} placeholder="غير محدد" /></Field>
                <Field label="تاريخ الميلاد"><Input type="date" value={form.birth_date} onChange={set('birth_date')} /></Field>
                <Field label="مكان الميلاد"><Input value={form.birth_place} onChange={set('birth_place')} /></Field>
                <Field label="الرقم الوطني"><Input value={form.national_id} onChange={set('national_id')} dir="ltr" /></Field>
                <Field label="هاتف الطالب"><PhoneInput value={form.student_phone} onChange={(v) => setForm((f) => ({ ...f, student_phone: v }))} /></Field>
                <Field label="رقم التسجيل">
                  {editing.id
                    ? <Input value={form.registration_number} onChange={set('registration_number')} dir="ltr" />
                    : <Input value="" disabled placeholder="يُعطى تلقائياً عند الحفظ" />}
                </Field>
                {isAdmin && <Field label="القسط الدراسي (شهرياً)"><Input type="number" value={form.tuition_fee} onChange={set('tuition_fee')} /></Field>}
                <Field label="الباص" hint={isAdmin ? 'اختر الباص لتعبئة قسط النقل تلقائياً، أو اتركه فارغاً لعدم استخدام مواصلات المدرسة.' : undefined}>
                  <Select value={form.transportation_id} onChange={setBusId} placeholder="بدون مواصلات" options={busOptions} />
                </Field>
                {isAdmin && (
                  <Field label="قسط النقل (شهرياً)">
                    <Input type="number" value={form.transport_fee} onChange={set('transport_fee')} disabled={!!form.transportation_id} />
                  </Field>
                )}
                <Field label="ملاحظات" span><Textarea value={form.notes} onChange={set('notes')} /></Field>
              </FormGrid>
            </FormSection>
          </form>
        </Modal>
      )}

      {transferring && (
        <Modal
          open
          size="sm"
          title={`نقل: ${transferring.name}`}
          onClose={() => setTransferring(null)}
          confirmClose={transferDirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button onClick={doTransfer} loading={busy} data-guide="modal-save">نقل</Button>
            </>
          )}
        >
          <p className="ui-modal-message" style={{ marginBottom: 'var(--ui-space-3)' }}>
            الشعبة الحالية: <strong>{transferring.section_title}</strong>
          </p>
          <Field label="الصف" hint="اتركه كما هو للنقل بين شعب الصف نفسه، أو اختر صفاً آخر." guide="move-grade">
            <Select
              value={newGradeId}
              onChange={(e) => { setNewGradeId(e.target.value); setNewSectionId(''); }}
              options={transferGrades}
            />
          </Field>
          <Field label="الشعبة الجديدة" required guide="move-section">
            <Select
              value={newSectionId}
              onChange={(e) => setNewSectionId(e.target.value)}
              placeholder="اختر الشعبة"
              options={sectionOptions(
                sections.filter((s) => s.id !== transferring.section_id && s.status === 'active' && String(s.grade_id) === newGradeId),
                { withCount: true }
              )}
            />
          </Field>
          {newGradeId !== transferFromGrade && (
            <p className="ui-modal-message">
              سينتقل الطالب إلى صف آخر. تفقّده وعلاماته في صفه السابق تبقى محفوظة في ملفه.
            </p>
          )}
        </Modal>
      )}

      {reactivating && (
        <ReactivateStudentModal
          student={reactivating}
          toast={toast}
          onClose={() => setReactivating(null)}
          onDone={() => { setReactivating(null); reload(); }}
        />
      )}

      {toDelete && (
        <Modal
          open
          size="sm"
          title="أرشفة الطالب"
          onClose={() => setToDelete(null)}
          confirmClose={archiveDirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="danger" onClick={doDelete} loading={busy}>تأكيد</Button>
            </>
          )}
        >
          <p className="ui-modal-message" style={{ marginBottom: 'var(--ui-space-3)' }}>
            سيتم إنهاء قيد «{toDelete.name}» وإيقاف حسابات الدخول المرتبطة به. لن تُحذف بياناته.
          </p>
          <Field label="سبب المغادرة" required>
            <Select value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} options={LEAVE_REASONS} />
          </Field>
        </Modal>
      )}
    </PageShell>
  );
}
